/**
 * Simple preview canvas showing component areas as outlined rectangles.
 * Supports 16:9 (landscape) and 9:16 (portrait) aspect ratios.
 */

import { Box, ToggleButton, ToggleButtonGroup, Typography } from '@mui/material';

export interface LayoutRect {
  x: number;
  y: number;
  w: number;
  h: number;
  label?: string;
}

interface LayoutPreviewCanvasProps {
  rects: LayoutRect[];
  aspectRatio: '16:9' | '9:16';
  onAspectRatioChange: (ratio: '16:9' | '9:16') => void;
}

export function LayoutPreviewCanvas({ rects, aspectRatio, onAspectRatioChange }: LayoutPreviewCanvasProps) {
  const isPortrait = aspectRatio === '9:16';
  const canvasWidth = isPortrait ? 180 : 320;
  const canvasHeight = isPortrait ? 320 : 180;

  return (
    <Box>
      <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 1 }}>
        <Typography variant="caption" color="text.secondary">
          Layout preview
        </Typography>
        <ToggleButtonGroup
          size="small"
          value={aspectRatio}
          exclusive
          onChange={(_, v) => v && onAspectRatioChange(v)}
          sx={{ '& .MuiToggleButton-root': { py: 0.25, px: 1 } }}
        >
          <ToggleButton value="16:9">16:9</ToggleButton>
          <ToggleButton value="9:16">9:16</ToggleButton>
        </ToggleButtonGroup>
      </Box>
      <Box
        sx={{
          width: canvasWidth,
          height: canvasHeight,
          bgcolor: 'action.hover',
          borderRadius: 1,
          position: 'relative',
          overflow: 'hidden',
        }}
      >
        {rects.map((r, i) => (
          <Box
            key={i}
            sx={{
              position: 'absolute',
              left: `${r.x}%`,
              top: `${r.y}%`,
              width: `${r.w}%`,
              height: `${r.h}%`,
              border: '1px dashed',
              borderColor: 'primary.main',
              borderRadius: 0.5,
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              fontSize: 10,
              color: 'primary.main',
            }}
          >
            {r.label ?? `${r.x},${r.y}`}
          </Box>
        ))}
      </Box>
    </Box>
  );
}
